import React, { useState, useRef, useEffect } from "react";

const InputDate = ({ value = "", onChange, className = "" }) => {
  const [inputValue, setInputValue] = useState(value);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [calendarTop, setCalendarTop] = useState(0);
  const inputRef = useRef(null);

  // Quản lý state cho năm, tháng, chế độ xem
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [viewMode, setViewMode] = useState("days"); // "days", "months", "years"

  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Cập nhật vị trí calendar khi mở
  useEffect(() => {
    if (isCalendarOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setCalendarTop(rect.height + 5);
    }
  }, [isCalendarOpen]);

  // Định dạng input thành dd/mm/yyyy
  const formatInput = (value) => {
    let cleaned = value.replace(/\D/g, ""); // Chỉ giữ số
    let formatted = "";
    if (cleaned.length > 0) formatted += cleaned.substring(0, 2);
    if (cleaned.length > 2) formatted += "/" + cleaned.substring(2, 4);
    if (cleaned.length > 4) formatted += "/" + cleaned.substring(4, 8);
    return formatted;
  };

  // Kiểm tra ngày hợp lệ
  const isValidDate = (dateString) => {
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateString.match(regex);
    if (!match) return false;
    const [day, month, year] = match.slice(1).map(Number);
    const date = new Date(year, month - 1, day);
    return (
      date.getDate() === day &&
      date.getMonth() === month - 1 &&
      date.getFullYear() === year
    );
  };

  // Xử lý khi nhập dữ liệu vào input
  const handleChange = (e) => {
    const formattedValue = formatInput(e.target.value);
    setInputValue(formattedValue);
    setIsCalendarOpen(false);

    if (isValidDate(formattedValue)) {
      onChange(formattedValue);
    }
  };

  // Sinh các ngày trong tháng
  const generateDaysInMonth = (year, month) => {
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < firstDayOfMonth; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
    return days;
  };

  const daysInMonth = generateDaysInMonth(currentYear, currentMonth);

  // Chọn ngày từ calendar
  const handleDateSelect = (date) => {
    const formattedDate = date.toLocaleDateString("en-GB"); // dd/mm/yyyy
    setInputValue(formattedDate);
    onChange(formattedDate);
    setIsCalendarOpen(false);
  };

  // Điều chỉnh tháng
  const changeMonth = (direction) => {
    let newMonth = currentMonth + direction;
    let newYear = currentYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear -= 1;
    }
    if (newMonth > 11) {
      newMonth = 0;
      newYear += 1;
    }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  // Điều chỉnh năm
  const changeYear = (direction) => {
    setCurrentYear(currentYear + direction);
  };

  // Chọn tháng
  const handleMonthSelect = (month) => {
    setCurrentMonth(month);
    setViewMode("days");
  };

  // Chọn năm
  const handleYearSelect = (year) => {
    setCurrentYear(year);
    setViewMode("months");
  };

  // Hiển thị 12 năm trong khoảng gần nhất
  const generateYears = () => {
    const startYear = Math.floor(currentYear / 10) * 10;
    return Array.from({ length: 12 }, (_, i) => startYear + i);
  };

  return (
    <div className="relative w-full">
      {/* Input field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`border border-gray-300 px-3 pr-10 w-full rounded-lg h-[52px] focus:border-blue-500 focus:ring-2 focus:ring-blue-300 outline-none ${className}`}
          placeholder="DD/MM/YYYY"
          value={inputValue}
          onChange={handleChange}
        />
        {/* Calendar icon */}
        <div
          className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
          onClick={() => setIsCalendarOpen(!isCalendarOpen)}
        >
          <img
            src="/icons/calendar.svg"
            alt="Calendar Icon"
            className="w-5 h-5"
          />
        </div>
      </div>

      {/* Calendar */}
      {isCalendarOpen && (
        <div
          className="absolute left-0 bg-white border border-gray-300 rounded-lg shadow-lg z-40 w-full"
          style={{ top: calendarTop, minWidth: "250px" }}
        >
          {/* Chế độ xem ngày */}
          {viewMode === "days" && (
            <>
              <div className="flex justify-between items-center p-2 bg-blue-500 rounded-t-lg">
                <button
                  onClick={() => changeMonth(-1)}
                  className="px-2 py-1 text-gray-700 hover:bg-blue-200 rounded-lg"
                >
                  <img src="/icons/left-arrow.svg" alt="Left arrow" />
                </button>
                <span
                  className="font-semibold text-white cursor-pointer"
                  onClick={() => setViewMode("months")}
                >
                  {months[currentMonth]} {currentYear}
                </span>
                <button
                  onClick={() => changeMonth(1)}
                  className="px-2 py-1 text-gray-700 hover:bg-blue-200 rounded-lg"
                >
                  <img src="/icons/right-arrow.svg" alt="Right arrow" />
                </button>
              </div>
              <div className="grid grid-cols-7 text-center font-semibold text-gray-700 p-1">
                {daysOfWeek.map((day) => (
                  <div key={day}>{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 text-center text-gray-600 p-2">
                {daysInMonth.map((date, index) =>
                  date ? (
                    <div
                      key={index}
                      className={`p-1 hover:bg-blue-200 rounded-lg cursor-pointer ${
                        inputValue === date.toLocaleDateString("en-GB")
                          ? "bg-blue-500 text-white"
                          : ""
                      }`}
                      onClick={() => handleDateSelect(date)}
                    >
                      {date.getDate()}
                    </div>
                  ) : (
                    <div key={index} className="p-2"></div>
                  )
                )}
              </div>
            </>
          )}
          {/* Chế độ xem tháng */}
          {viewMode === "months" && (
            <>
              <div className="flex justify-between items-center p-2 bg-blue-500 rounded-t-lg">
                <button
                  onClick={() => changeYear(-1)}
                  className="px-2 py-1 text-gray-700 hover:bg-blue-200 rounded-lg"
                >
                  <img src="/icons/left-arrow.svg" alt="Left arrow" />
                </button>
                <span
                  className="font-semibold text-white cursor-pointer"
                  onClick={() => setViewMode("years")}
                >
                  {currentYear}
                </span>
                <button
                  onClick={() => changeYear(1)}
                  className="px-2 py-1 text-gray-700 hover:bg-blue-200 rounded-lg"
                >
                  <img src="/icons/right-arrow.svg" alt="Right arrow" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 p-4">
                {months.map((month, index) => (
                  <div
                    key={index}
                    className="p-2 text-center text-gray-700 hover:bg-blue-200 rounded-lg cursor-pointer"
                    onClick={() => handleMonthSelect(index)}
                  >
                    {month}
                  </div>
                ))}
              </div>
            </>
          )}

          {viewMode === "years" && (
            <>
              <div className="flex justify-between items-center p-2 bg-blue-500 rounded-t-lg">
                <button
                  onClick={() => changeYear(-10)}
                  className="px-2 py-1 text-gray-700 hover:bg-blue-200 rounded-lg"
                >
                  <img src="/icons/left-arrow.svg" alt="Left arrow" />
                </button>
                <span className="font-semibold text-white cursor-pointer">
                  {generateYears()[0]} - {generateYears()[11]}
                </span>
                <button
                  onClick={() => changeYear(10)}
                  className="px-2 py-1 text-gray-700 hover:bg-blue-200 rounded-lg"
                >
                  <img src="/icons/right-arrow.svg" alt="Right arrow" />
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 p-4">
                {generateYears().map((year) => (
                  <div
                    key={year}
                    className="p-2 text-center text-gray-700 hover:bg-blue-200 rounded-lg cursor-pointer"
                    onClick={() => handleYearSelect(year)}
                  >
                    {year}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default InputDate;
